import { useState } from "react";
import carouselImage from "../assets/images/trading/buy_sell_carousel_item.svg";
import { Button } from "./ui/button";
import { PlusIcon } from "lucide-react";
import { SecuredFooter } from "./ui/secured-footer";

interface EightyEightPayCarouselProps {
  onGetStarted: () => void;
}

const EightyEightPayCarousel = ({
  onGetStarted,
}: EightyEightPayCarouselProps) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = [
    {
      id: 0,
      title: "Free, Instant Payments.",
      description:
        "Whether they're in the next room or across the world, it's just as easy.",
      image: carouselImage,
    },
    {
      id: 1,
      title: "Keep the Full Amount",
      description:
        "Your money goes where you send it. Without extra hands in the middle.",
      image: carouselImage,
    },
    {
      id: 2,
      title: "Couple Taps, Job Done.",
      description: "Settle payments in seconds and move on with your day.",
      image: carouselImage,
    },
  ];

  const handleNext = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlide = slides.at(activeSlide) ?? slides[0];
  const isLastSlide = activeSlide === slides.length - 1;
  const isFirstSlide = activeSlide === 0;

  return (
    <div className="flex flex-col h-full min-h-[700px]">
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-16">
        <div className="flex flex-col items-center justify-center text-center space-y-4 max-w-[556px]">
          <div className="flex items-center justify-center mb-2">
            <img
              src={currentSlide.image}
              alt={currentSlide.title}
              className="w-[133px] lg:w-[194px]"
            />
          </div>

          <h3 className="text-[24px] font-semibold text-[#33125F] whitespace-pre-line">
            {currentSlide.title}
          </h3>

          <p className="text-[14px] text-[#423E51] leading-relaxed max-w-sm text-center">
            {currentSlide.description}
          </p>
        </div>

        <div className="flex justify-center gap-4 mt-8 mb-10">
          {!isFirstSlide && (
            <Button
              onClick={handlePrevious}
              variant="ghost"
              size="lg"
              className="text-sm border border-[#EBE9EF] px-6 py-3 min-w-[140px] rounded-full text-[#6D6584] font-semibold h-[48px]"
            >
              Previous
            </Button>
          )}
          {isLastSlide ? (
            <Button
              className="btn-primary text-sm px-6 py-3 min-w-[140px] h-[48px]"
              onClick={onGetStarted}
            >
              <PlusIcon className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-white text-sm font-semibold">
                Get Started
              </span>
            </Button>
          ) : (
            <Button
              className="btn-primary text-sm px-6 py-3 h-[48px]"
              onClick={handleNext}
            >
              <PlusIcon className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-white text-sm font-semibold">Next</span>
            </Button>
          )}
        </div>

        <div className="flex justify-center space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              className={`h-2 rounded-full transition-colors duration-300 ${
                index === activeSlide ? "bg-[#010317] w-6" : "bg-[#EBE9EF] w-2"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <SecuredFooter className="mt-0" />
    </div>
  );
};

export default EightyEightPayCarousel;
